import { React, useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Card,
	Row,
	Col,
	Tag,
	Button,
	Spin,
	message,
	Typography,
	Space,
	Avatar,
	Input,
	DatePicker,
	Select,
	Form,
} from "antd";
import dayjs from "dayjs";
import {
	ArrowLeftOutlined,
	UserOutlined,
	CalendarOutlined,
	PhoneOutlined,
	HomeOutlined,
	TeamOutlined,
	FileTextOutlined,
	SaveOutlined,
	EditOutlined,
	HeartOutlined,
	HistoryOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import EditClassificationModal from "./EditClassificationModal";
import EditStatusModal from "./EditStatusModal";
import EditBaptismalStatusModal from "./EditBaptismalStatusModal";
import EditCellModal from "./EditCellModal";
import EditDepartmentModal from "./EditDepartmentModal";
import SearchMemberModal from "./SearchMemberModal";
import {
	ViewClassificationChangeRecordsModal,
	ViewStatusChangeRecordsModal,
	ViewBaptismalStatusChangeRecordsModal,
	ViewCellChangeRecordsModal,
	ViewAffiliatedDepartmentChangeRecordsModal,
	ViewDisciplinaryRecordsModal,
	EditFamilyRelationshipModal,
	EditServingDepartmentModal,
	ViewServingDepartmentChangeRecordsModal,
} from "./index";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EditMemberData = () => {
	const isMobile = useIsMobile();
	const { id } = useParams();
	const navigate = useNavigate();
	const [memberData, setMemberData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [form] = Form.useForm();

	// Modal states for popup editing
	const [statusModalVisible, setStatusModalVisible] = useState(false);
	const [baptismalModalVisible, setBaptismalModalVisible] =
		useState(false);
	const [classificationModalVisible, setClassificationModalVisible] =
		useState(false);
	const [departmentModalVisible, setDepartmentModalVisible] =
		useState(false);
	const [cellModalVisible, setCellModalVisible] = useState(false);

	// Modal visibility states for viewing records
	const [classificationRecordsVisible, setClassificationRecordsVisible] =
		useState(false);
	const [statusRecordsVisible, setStatusRecordsVisible] = useState(false);
	const [
		baptismalStatusRecordsVisible,
		setBaptismalStatusRecordsVisible,
	] = useState(false);
	const [cellRecordsVisible, setCellRecordsVisible] = useState(false);
	const [departmentRecordsVisible, setDepartmentRecordsVisible] =
		useState(false);
	const [disciplinaryRecordsVisible, setDisciplinaryRecordsVisible] =
		useState(false);
	const [
		familyRelationshipModalVisible,
		setFamilyRelationshipModalVisible,
	] = useState(false);
	const [
		servingDepartmentModalVisible,
		setServingDepartmentModalVisible,
	] = useState(false);
	const [
		servingDepartmentRecordsVisible,
		setServingDepartmentRecordsVisible,
	] = useState(false);
	const [searchMemberModalVisible, setSearchMemberModalVisible] =
		useState(false);

	const getMemberDetail = useCallback(async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				`/users/user-detail/${id}/`
			);
			if (response.status === 200) {
				setMemberData(response.data);
				form.setFieldsValue({
					id: response.data.id,
					name: response.data.name,
					nickname: response.data.nickname,
					birthdate: response.data.birthdate
						? dayjs(response.data.birthdate)
						: null,
					gender: response.data.gender,
					phone_number:
						response.data.phone_number,
					address: response.data.address,
					address_detail:
						response.data.address_detail,
					job: response.data.job,
					led_by_id: response.data.led_by_id,
					led_by_name: response.data.led_by_name,
					date_of_registration: response.data
						.date_of_registration
						? response.data
								.date_of_registration
						: null,
					date_of_passing: response.data
						.date_of_passing
						? response.data.date_of_passing
						: null,
					etc: response.data.etc,
				});
			}
		} catch (error) {
			message.error("성도 정보를 불러오는데 실패했습니다.");
			navigate("../view-all-members");
		} finally {
			setLoading(false);
		}
	}, [id, navigate, form]);

	useEffect(() => {
		getMemberDetail();
	}, [getMemberDetail]);

	const handleBack = () => {
		navigate(`../view-member-detail/${id}`);
	};

	const formatPhoneNumber = (phoneNumber) => {
		if (!phoneNumber) return phoneNumber;

		// Remove all non-digit characters
		const digits = phoneNumber.replace(/\D/g, "");

		// Format based on length
		if (digits.length === 10) {
			// 0101234567 -> 010-1234-567
			return digits.replace(
				/(\d{3})(\d{4})(\d{3})/,
				"$1-$2-$3"
			);
		} else if (digits.length === 11) {
			// 01012345678 -> 010-1234-5678
			return digits.replace(
				/(\d{3})(\d{4})(\d{4})/,
				"$1-$2-$3"
			);
		}

		// If no specific format matches, return as is
		return phoneNumber;
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			const values = await form.validateFields();

			const updateData = {
				...values,
				nickname: values.nickname || null,
				phone_number: formatPhoneNumber(
					values.phone_number
				),
				birthdate: values.birthdate
					? values.birthdate.format("YYYY-MM-DD")
					: null,
				date_of_registration:
					values.date_of_registration
						? values.date_of_registration
						: null,
				date_of_passing: values.date_of_passing
					? values.date_of_passing
					: null,
				led_by_id: values.led_by_id || null,
				led_by_name: values.led_by_name || null,
			};

			const response = await useAxios.put(
				`/users/user-detail/${id}/`,
				updateData
			);

			if (response.status === 200) {
				message.success(
					"성도 정보가 성공적으로 수정되었습니다."
				);
				navigate(`../view-member-detail/${id}`);
			}
		} catch (error) {
			message.error("성도 정보 수정에 실패했습니다.");
			console.error(error);
		} finally {
			setSaving(false);
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "입교인":
				return "blue";
			case "학습인":
				return "green";
			case "유아세례교인":
				return "orange";
			case "실종교인":
				return "red";
			case "별세교인":
				return "default";
			default:
				return "default";
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const InfoItem = ({ label, value, icon, tagColor, isTag = false }) => (
		<div className="member-detail__info-item">
			<Text className="member-detail__info-label">
				{label}:
			</Text>
			<div className="member-detail__info-value">
				{icon && (
					<span className="member-detail__info-icon">
						{icon}
					</span>
				)}
				{isTag ? (
					<Tag color={tagColor}>
						{value || "-"}
					</Tag>
				) : (
					<Text strong>{value || "-"}</Text>
				)}
			</div>
		</div>
	);

	const EditableInfoItem = ({
		label,
		name,
		icon,
		type = "input",
		options = [],
		readOnly = false,
	}) => (
		<div className="member-detail__info-item">
			<Text className="member-detail__info-label">
				{label}:
			</Text>
			<div className="member-detail__info-value">
				{icon && (
					<span className="member-detail__info-icon">
						{icon}
					</span>
				)}
				<Form.Item
					name={name}
					style={{ margin: 0, flex: 1 }}>
					{type === "select" ? (
						<Select
							placeholder={`${label} 선택`}>
							{options.map(
								(option) => (
									<Option
										key={
											option.value
										}
										value={
											option.value
										}>
										{
											option.label
										}
									</Option>
								)
							)}
						</Select>
					) : type === "date" ? (
						<DatePicker
							inputReadOnly={isMobile}
							placeholder={`${label} 선택`}
							style={{
								width: "100%",
							}}
							format="YYYY-MM-DD"
						/>
					) : type === "textarea" ? (
						<TextArea
							placeholder={`${label} 입력`}
							rows={3}
						/>
					) : (
						<Input
							placeholder={`${label} 입력`}
						/>
					)}
				</Form.Item>
			</div>
		</div>
	);

	const PopupButton = ({
		label,
		value,
		onClick,
		tagColor,
		onHistoryClick,
	}) => (
		<div className="member-detail__info-item">
			<Text className="member-detail__info-label">
				{label}:
			</Text>
			<div className="member-detail__info-value">
				<Tag
					color={tagColor}
					style={{ marginRight: 8 }}>
					{value || "-"}
				</Tag>
				<Button
					type="primary"
					size="small"
					icon={<EditOutlined />}
					onClick={onClick}>
					수정
				</Button>
				{onHistoryClick && (
					<Button
						type="text"
						size="small"
						icon={<HistoryOutlined />}
						onClick={onHistoryClick}
						title="변경 기록 보기"
						style={{ marginLeft: 4 }}
					/>
				)}
			</div>
		</div>
	);

	const FamilyMemberCard = ({
		member,
		relationship,
		onClick,
		isSpouse = false,
	}) => (
		<Card
			size="small"
			className="family-member-card"
			onClick={onClick}
			style={{
				cursor: onClick ? "pointer" : "default",
				border: isSpouse
					? "1px solid #d9d9d9"
					: undefined,
			}}>
			<div style={{ textAlign: "center" }}>
				<Avatar
					size={isSpouse ? 30 : 40}
					icon={<UserOutlined />}
					style={{
						backgroundColor:
							member.gender === "남"
								? "#2457e6"
								: "#eb2f96",
						marginBottom: 8,
					}}
				/>
				<div
					style={{
						fontSize: isSpouse
							? "10px"
							: "12px",
						fontWeight: "bold",
					}}>
					{member.name}
				</div>
				<div
					style={{
						fontSize: isSpouse
							? "8px"
							: "10px",
						color: "#666",
					}}>
					{member.birthdate
						? new Date(
								member.birthdate
						  ).toLocaleDateString("ko-KR")
						: "-"}
				</div>
				<Tag size="small" color="blue">
					{relationship}
				</Tag>
			</div>
		</Card>
	);

	const ChildWithSpouseCard = ({ child, onClick }) => (
		<div style={{ textAlign: "center" }}>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				{child.gender === "남" ? (
					<>
						<FamilyMemberCard
							member={child}
							relationship="자녀"
							onClick={() =>
								onClick(
									child.id
								)
							}
						/>
						{child.spouse && (
							<>
								<div className="spouse-connector"></div>
								<FamilyMemberCard
									member={
										child.spouse
									}
									relationship="배우자"
									onClick={() =>
										onClick(
											child
												.spouse
												.id
										)
									}
									isSpouse={
										true
									}
								/>
							</>
						)}
					</>
				) : (
					<>
						{child.spouse && (
							<>
								<FamilyMemberCard
									member={
										child.spouse
									}
									relationship="배우자"
									onClick={() =>
										onClick(
											child
												.spouse
												.id
										)
									}
									isSpouse={
										true
									}
								/>

								<div className="spouse-connector"></div>
							</>
						)}

						<FamilyMemberCard
							member={child}
							relationship="자녀"
							onClick={() =>
								onClick(
									child.id
								)
							}
						/>
					</>
				)}
			</div>
		</div>
	);

	const handleFamilyMemberClick = (memberId) => {
		navigate(`../view-member-detail/${memberId}`);
	};

	const handleStatusModalSuccess = () => {
		getMemberDetail(); // Refresh member data
		setStatusModalVisible(false);
	};

	const handleBaptismalModalSuccess = () => {
		getMemberDetail(); // Refresh member data
		setBaptismalModalVisible(false);
	};

	const handleClassificationModalSuccess = () => {
		getMemberDetail(); // Refresh member data
		setClassificationModalVisible(false);
	};

	const handleDepartmentModalSuccess = () => {
		getMemberDetail(); // Refresh member data
		setDepartmentModalVisible(false);
	};

	const handleCellModalSuccess = () => {
		getMemberDetail(); // Refresh member data
		setCellModalVisible(false);
	};

	const handleFamilyRelationshipModalSuccess = () => {
		getMemberDetail(); // Refresh member data
		setFamilyRelationshipModalVisible(false);
	};

	const handleServingDepartmentModalSuccess = () => {
		getMemberDetail(); // Refresh member data
		setServingDepartmentModalVisible(false);
	};

	const handleMemberSelect = (member) => {
		form.setFieldsValue({
			led_by_id: member.id,
			led_by_name: member.name,
		});
	};

	if (loading) {
		return (
			<div className="member-detail__loading">
				<Spin size="large" />
				<Text>성도 정보를 불러오는 중...</Text>
			</div>
		);
	}

	if (!memberData) {
		return (
			<div className="member-detail__error">
				<Text type="danger">
					성도 정보를 찾을 수 없습니다.
				</Text>
				<Button onClick={handleBack}>
					목록으로 돌아가기
				</Button>
			</div>
		);
	}

	// Sort parents: father (male) on left, mother (female) on right
	const sortedParents = memberData.parents
		? memberData.parents.sort((a, b) => {
				if (a.gender === "남" && b.gender === "여")
					return -1;
				if (a.gender === "여" && b.gender === "남")
					return 1;
				return 0;
		  })
		: [];

	// Sort spouse's parents: father (male) on left, mother (female) on right
	const sortedSpouseParents = memberData.spouse?.parents
		? memberData.spouse.parents.sort((a, b) => {
				if (a.gender === "남" && b.gender === "여")
					return -1;
				if (a.gender === "여" && b.gender === "남")
					return 1;
				return 0;
		  })
		: [];

	// Sort children by age (birthdate)
	const sortedChildren = memberData.children
		? memberData.children.sort((a, b) => {
				if (!a.birthdate) return 1;
				if (!b.birthdate) return -1;
				return (
					new Date(a.birthdate) -
					new Date(b.birthdate)
				);
		  })
		: [];

	return (
		<>
			<PageTitle title="성도 정보 수정" />
			<div className="member-detail__container">
				<div className="member-detail__header">
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={handleBack}
						className="member-detail__back-button">
						상세정보로 돌아가기
					</Button>
					<Title
						level={2}
						className="member-detail__title">
						성도 정보 수정
					</Title>
					<Button
						icon={<SaveOutlined />}
						onClick={handleSave}
						loading={saving}
						type="primary"
						className="member-detail__edit-button">
						저장
					</Button>
				</div>

				<Form form={form} layout="vertical">
					<div className="member-detail__content">
						{/* 기본 정보 섹션 */}
						<Card
							title={
								<Space>
									<UserOutlined />
									기본
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								{/* ID is read only */}
								<Col
									xs={24}
									sm={12}
									md={8}>
									<InfoItem
										label="ID"
										name="id"
										value={
											memberData.id
										}
										readOnly={
											true
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="성명"
										name="name"
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="별명"
										name="nickname"
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="성별"
										name="gender"
										type="select"
										options={[
											{
												value: "남",
												label: "남",
											},
											{
												value: "여",
												label: "여",
											},
										]}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="생년월일"
										name="birthdate"
										type="date"
										icon={
											<CalendarOutlined />
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="연락처"
										name="phone_number"
										icon={
											<PhoneOutlined />
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="직업"
										name="job"
									/>
								</Col>
							</Row>
							<Row
								gutter={[
									24, 16,
								]}
								style={{
									marginTop: 16,
								}}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											인도자
											ID:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="led_by_id"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<Input
													placeholder="인도자 ID 입력"
													readOnly
												/>
											</Form.Item>
											{memberData.led_by_id && (
												<Button
													type="primary"
													size="small"
													icon={
														<UserOutlined />
													}
													onClick={() =>
														navigate(
															`../view-member-detail/${memberData.led_by_id}`
														)
													}
													style={{
														marginLeft: 8,
													}}
													title="인도자 상세정보 보기">
													이동
												</Button>
											)}
										</div>
									</div>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											인도자:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="led_by_name"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<Input placeholder="인도자 이름 입력" />
											</Form.Item>
											<Button
												type="primary"
												size="small"
												icon={
													<SearchOutlined />
												}
												onClick={() =>
													setSearchMemberModalVisible(
														true
													)
												}
												style={{
													marginLeft: 8,
												}}>
												조회
											</Button>
										</div>
									</div>
								</Col>
							</Row>
						</Card>
						{memberData.spouse && (
							<Card
								title={
									<Space>
										<TeamOutlined />
										배우자
										정보
									</Space>
								}
								className="member-detail__card">
								<Row
									gutter={[
										24,
										16,
									]}>
									<Col
										xs={
											24
										}
										sm={
											12
										}>
										<InfoItem
											label="배우자"
											value={
												memberData
													.spouse
													?.name
													? memberData
															.spouse
															.name +
													  "(" +
													  memberData
															.spouse
															.id +
													  ")"
													: "-"
											}
										/>
									</Col>
									<Col
										xs={
											24
										}
										sm={
											12
										}>
										<Button
											type="primary"
											size="small"
											icon={
												<UserOutlined />
											}
											onClick={() =>
												navigate(
													`../view-member-detail/${memberData.spouse.id}`
												)
											}
											title="배우자 상세정보 보기">
											이동
										</Button>
									</Col>
								</Row>
							</Card>
						)}

						{/* 주소 정보 섹션 */}
						<Card
							title={
								<Space>
									<HomeOutlined />
									주소
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}>
									<EditableInfoItem
										label="주소"
										name="address"
									/>
								</Col>
								<Col
									xs={24}
									sm={12}>
									<EditableInfoItem
										label="상세주소"
										name="address_detail"
									/>
								</Col>
							</Row>
						</Card>

						{/* 교회 정보 섹션 */}
						<Card
							title={
								<Space>
									<TeamOutlined />
									교회
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<PopupButton
										label="분류"
										value={
											memberData.classification_name
										}
										tagColor={getClassificationColor(
											memberData.classification_name
										)}
										onClick={() =>
											setClassificationModalVisible(
												true
											)
										}
										onHistoryClick={() =>
											setClassificationRecordsVisible(
												true
											)
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<PopupButton
										label="상태"
										value={
											memberData.status_name
										}
										tagColor={getStatusColor(
											memberData.status_name
										)}
										onClick={() =>
											setStatusModalVisible(
												true
											)
										}
										onHistoryClick={() =>
											setStatusRecordsVisible(
												true
											)
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="등록일"
										name="date_of_registration"
										type="text"
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<PopupButton
										label="세례상태"
										value={
											memberData.baptismal_status
										}
										tagColor="blue"
										onClick={() =>
											setBaptismalModalVisible(
												true
											)
										}
										onHistoryClick={() =>
											setBaptismalStatusRecordsVisible(
												true
											)
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}>
										<InfoItem
											label="징계상태"
											value={
												memberData.disciplinary_status
											}
										/>
										<Button
											type="text"
											size="small"
											icon={
												<HistoryOutlined />
											}
											onClick={() =>
												setDisciplinaryRecordsVisible(
													true
												)
											}
											title="징계 기록 보기"
										/>
									</div>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<EditableInfoItem
										label="별세일"
										name="date_of_passing"
										type="text"
									/>
								</Col>
							</Row>
						</Card>

						{/* 소속 정보 섹션 */}
						<Card
							title={
								<Space>
									<TeamOutlined />
									소속
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<PopupButton
										label="구역"
										value={
											memberData.cell_name
												? memberData.district_name +
												  "대 교구 " +
												  memberData.zone_name +
												  "지구 " +
												  memberData.cell_name +
												  "구역"
												: "구역 없음"
										}
										tagColor="blue"
										onClick={() =>
											setCellModalVisible(
												true
											)
										}
										onHistoryClick={() =>
											setCellRecordsVisible(
												true
											)
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<PopupButton
										label="소속기관"
										value={
											memberData.affiliated_department_name
										}
										tagColor="green"
										onClick={() =>
											setDepartmentModalVisible(
												true
											)
										}
										onHistoryClick={() =>
											setDepartmentRecordsVisible(
												true
											)
										}
									/>
								</Col>
							</Row>
						</Card>

						{/* 봉사기관 정보 섹션 */}
						<Card
							title={
								<Space>
									<TeamOutlined />
									봉사기관
									정보
								</Space>
							}
							className="member-detail__card"
							extra={
								<Button
									type="primary"
									icon={
										<TeamOutlined />
									}
									onClick={() =>
										setServingDepartmentModalVisible(
											true
										)
									}
									size="small">
									봉사기관
									편집
								</Button>
							}>
							<Row gutter={[24, 16]}>
								{memberData.serving_departments &&
								memberData
									.serving_departments
									.length >
									0 ? (
									memberData.serving_departments.map(
										(
											dept,
											index
										) => (
											<Col
												key={
													dept.id
												}
												xs={
													24
												}
												sm={
													12
												}
												md={
													8
												}>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "8px",
													}}>
													<InfoItem
														label={`봉사기관 ${
															index +
															1
														}`}
														value={
															dept.department_name
														}
													/>
													<Tag
														style={{
															marginBottom:
																"8px",
														}}
														color={
															dept.status ===
															"활동"
																? "green"
																: dept.status ===
																  "휴직"
																? "orange"
																: dept.status ===
																  "복직"
																? "blue"
																: "default"
														}
														size="small">
														{
															dept.status
														}
													</Tag>
												</div>
											</Col>
										)
									)
								) : (
									<Col
										span={
											24
										}>
										<Text type="secondary">
											등록된
											봉사기관이
											없습니다.
										</Text>
									</Col>
								)}
							</Row>
							<Row
								style={{
									marginTop: 16,
								}}>
								<Col span={24}>
									<Button
										type="text"
										size="small"
										icon={
											<HistoryOutlined />
										}
										onClick={() =>
											setServingDepartmentRecordsVisible(
												true
											)
										}
										title="봉사기관 변경 기록 보기">
										봉사기관
										변경
										기록
										보기
									</Button>
								</Col>
							</Row>
						</Card>

						{/* 기타 정보 섹션 */}

						<Card
							title={
								<Space>
									<FileTextOutlined />
									기타
									정보
								</Space>
							}
							className="member-detail__card">
							<Row>
								<Col span={24}>
									<EditableInfoItem
										label="메모"
										name="etc"
										type="textarea"
									/>
								</Col>
							</Row>
						</Card>

						{/* 가족 관계도 섹션 */}
						<Card
							title={
								<Space>
									<HeartOutlined />
									가족
									관계도
								</Space>
							}
							className="member-detail__card"
							extra={
								<Button
									type="primary"
									icon={
										<HeartOutlined />
									}
									onClick={() =>
										setFamilyRelationshipModalVisible(
											true
										)
									}
									size="small">
									가족
									관계
									편집
								</Button>
							}>
							<div
								style={{
									padding: "20px 0",
								}}>
								{/* Parents Row - Member's Parents and Spouse's Parents */}
								{(sortedParents.length >
									0 ||
									sortedSpouseParents.length >
										0) && (
									<Row
										justify="center"
										style={{
											marginBottom:
												"30px",
										}}>
										{/* First group: Member's parents (if male) or Spouse's parents (if female) */}
										{memberData.gender ===
										"남" ? (
											// Male member: Member's parents on left, Spouse's parents on right
											<>
												{/* Member's Parents */}
												{sortedParents.length >
													0 && (
													<Col
														style={{
															marginRight:
																sortedSpouseParents.length >
																0
																	? "40px"
																	: "0",
															textAlign: "center",
														}}>
														<Text
															style={{
																fontSize: "12px",
																color: "#666",
																fontStyle: "italic",
																marginBottom:
																	"10px",
																display: "block",
															}}>
															본인
															부모
														</Text>
														<Row
															justify="center"
															align="middle">
															{sortedParents.map(
																(
																	parent,
																	index
																) => (
																	<>
																		<Col
																			key={
																				parent.id
																			}>
																			<FamilyMemberCard
																				member={
																					parent
																				}
																				relationship="부모"
																				onClick={() =>
																					handleFamilyMemberClick(
																						parent.id
																					)
																				}
																			/>
																		</Col>
																		{index ===
																			0 &&
																			sortedParents.length >
																				1 && (
																				<div
																					className="spouse-connector"
																					style={{
																						width: "40px",
																						height: "2px",
																						backgroundColor:
																							"#d9d9d9",
																						margin: "8px auto",
																					}}
																				/>
																			)}
																	</>
																)
															)}
														</Row>
													</Col>
												)}

												{/* Spouse's Parents */}
												{sortedSpouseParents.length >
													0 && (
													<Col
														style={{
															marginLeft:
																sortedParents.length >
																0
																	? "40px"
																	: "0",
															textAlign: "center",
														}}>
														<Text
															style={{
																fontSize: "12px",
																color: "#666",
																fontStyle: "italic",
																marginBottom:
																	"10px",
																display: "block",
															}}>
															배우자
															부모
														</Text>
														<Row
															justify="center"
															align="middle">
															{sortedSpouseParents.map(
																(
																	parent,
																	index
																) => (
																	<>
																		<Col
																			key={
																				parent.id
																			}>
																			<FamilyMemberCard
																				member={
																					parent
																				}
																				relationship="배우자부모"
																				onClick={() =>
																					handleFamilyMemberClick(
																						parent.id
																					)
																				}
																			/>
																		</Col>
																		{/* Add spouse connector between parents */}
																		{index ===
																			0 &&
																			sortedSpouseParents.length >
																				1 && (
																				<div
																					className="spouse-connector"
																					style={{
																						width: "40px",
																						height: "2px",
																						backgroundColor:
																							"#d9d9d9",
																						margin: "8px auto",
																					}}
																				/>
																			)}
																	</>
																)
															)}
														</Row>
													</Col>
												)}
											</>
										) : (
											// Female member: Spouse's parents on left, Member's parents on right
											<>
												{/* Spouse's Parents */}
												{sortedSpouseParents.length >
													0 && (
													<Col
														style={{
															marginRight:
																sortedParents.length >
																0
																	? "40px"
																	: "0",
															textAlign: "center",
														}}>
														<Text
															style={{
																fontSize: "12px",
																color: "#666",
																fontStyle: "italic",
																marginBottom:
																	"10px",
																display: "block",
															}}>
															배우자
															부모
														</Text>
														<Row
															justify="center"
															align="middle">
															{sortedSpouseParents.map(
																(
																	parent,
																	index
																) => (
																	<>
																		<Col
																			key={
																				parent.id
																			}>
																			<FamilyMemberCard
																				member={
																					parent
																				}
																				relationship="배우자부모"
																				onClick={() =>
																					handleFamilyMemberClick(
																						parent.id
																					)
																				}
																			/>
																		</Col>
																		{/* Add spouse connector between parents */}
																		{index ===
																			0 &&
																			sortedSpouseParents.length >
																				1 && (
																				<div
																					className="spouse-connector"
																					style={{
																						width: "40px",
																						height: "2px",
																						backgroundColor:
																							"#d9d9d9",
																						margin: "8px auto",
																					}}
																				/>
																			)}
																	</>
																)
															)}
														</Row>
													</Col>
												)}

												{/* Member's Parents */}
												{sortedParents.length >
													0 && (
													<Col
														style={{
															marginLeft:
																sortedSpouseParents.length >
																0
																	? "40px"
																	: "0",
															textAlign: "center",
														}}>
														<Text
															style={{
																fontSize: "12px",
																color: "#666",
																fontStyle: "italic",
																marginBottom:
																	"10px",
																display: "block",
															}}>
															본인
															부모
														</Text>
														<Row
															justify="center"
															align="middle">
															{sortedParents.map(
																(
																	parent,
																	index
																) => (
																	<Col
																		key={
																			parent.id
																		}>
																		<FamilyMemberCard
																			member={
																				parent
																			}
																			relationship="부모"
																			onClick={() =>
																				handleFamilyMemberClick(
																					parent.id
																				)
																			}
																		/>
																		{/* Add spouse connector between parents */}
																		{index ===
																			0 &&
																			sortedParents.length >
																				1 && (
																				<div
																					className="spouse-connector"
																					style={{
																						width: "40px",
																						height: "2px",
																						backgroundColor:
																							"#d9d9d9",
																						margin: "8px auto",
																					}}
																				/>
																			)}
																	</Col>
																)
															)}
														</Row>
													</Col>
												)}
											</>
										)}
									</Row>
								)}

								{/* Current User and Spouse Row */}
								<Row
									justify="center"
									align="middle"
									style={{
										marginBottom:
											"30px",
									}}>
									{/* If user is male, show spouse on right; if female, show spouse on left */}
									{memberData.spouse &&
										memberData.gender ===
											"여" && (
											<Col>
												<FamilyMemberCard
													member={
														memberData.spouse
													}
													relationship="배우자"
													onClick={() =>
														handleFamilyMemberClick(
															memberData
																.spouse
																.id
														)
													}
												/>
											</Col>
										)}

									{/* Spouse connector line */}
									{memberData.spouse &&
										memberData.gender ===
											"여" && (
											<div className="spouse-connector"></div>
										)}

									{/* Current User */}
									<Col>
										<Card
											size="small"
											className="family-member-card current-user"
											style={{
												border: "2px solid #2457e6",
												backgroundColor:
													"#f0f8ff",
											}}>
											<div
												style={{
													textAlign: "center",
												}}>
												<Avatar
													size={
														50
													}
													icon={
														<UserOutlined />
													}
													style={{
														backgroundColor:
															memberData.gender ===
															"남"
																? "#2457e6"
																: "#eb2f96",
														marginBottom: 8,
													}}
												/>
												<div
													style={{
														fontSize: "14px",
														fontWeight: "bold",
													}}>
													{
														memberData.name
													}
												</div>
												<div
													style={{
														fontSize: "12px",
														color: "#666",
													}}>
													{memberData.birthdate
														? new Date(
																memberData.birthdate
														  ).toLocaleDateString(
																"ko-KR"
														  )
														: "-"}
												</div>
												<Tag
													size="small"
													color="green">
													본인
												</Tag>
											</div>
										</Card>
									</Col>

									{/* Spouse connector line for male user */}
									{memberData.spouse &&
										memberData.gender ===
											"남" && (
											<div className="spouse-connector"></div>
										)}

									{/* If user is male, show spouse on right */}
									{memberData.spouse &&
										memberData.gender ===
											"남" && (
											<Col
												style={{}}>
												<FamilyMemberCard
													member={
														memberData.spouse
													}
													relationship="배우자"
													onClick={() =>
														handleFamilyMemberClick(
															memberData
																.spouse
																.id
														)
													}
												/>
											</Col>
										)}
								</Row>

								{/* Children Row */}
								{sortedChildren.length >
									0 && (
									<Row justify="center">
										{sortedChildren.map(
											(
												child,
												index
											) => (
												<Col
													key={
														child.id
													}
													style={{
														margin: "0 10px",
													}}>
													<ChildWithSpouseCard
														child={
															child
														}
														onClick={
															handleFamilyMemberClick
														}
													/>
												</Col>
											)
										)}
									</Row>
								)}

								{/* No family members message */}
								{sortedParents.length ===
									0 &&
									!memberData.spouse &&
									sortedChildren.length ===
										0 &&
									sortedSpouseParents.length ===
										0 && (
										<div
											style={{
												textAlign: "center",
												color: "#999",
												padding: "40px 0",
											}}>
											<Text>
												등록된
												가족
												관계가
												없습니다.
											</Text>
										</div>
									)}
							</div>
						</Card>
					</div>
				</Form>
			</div>

			{/* Status Modal */}
			<EditStatusModal
				visible={statusModalVisible}
				onCancel={() => setStatusModalVisible(false)}
				onSuccess={handleStatusModalSuccess}
				userId={id}
				currentStatus={memberData?.status?.id}
			/>

			{/* Baptismal Status Modal */}
			<EditBaptismalStatusModal
				visible={baptismalModalVisible}
				onCancel={() => setBaptismalModalVisible(false)}
				onSuccess={handleBaptismalModalSuccess}
				userId={id}
				currentBaptismalStatus={
					memberData?.baptismal_status
				}
			/>

			{/* Classification Modal */}
			<EditClassificationModal
				visible={classificationModalVisible}
				onCancel={() =>
					setClassificationModalVisible(false)
				}
				onSuccess={handleClassificationModalSuccess}
				userId={id}
				currentClassification={
					memberData?.classification?.id
				}
			/>

			{/* Department Modal */}
			<EditDepartmentModal
				visible={departmentModalVisible}
				onCancel={() =>
					setDepartmentModalVisible(false)
				}
				onSuccess={handleDepartmentModalSuccess}
				userId={id}
				currentDepartmentId={
					memberData?.affiliated_department?.id
				}
			/>

			{/* Cell Modal */}
			<EditCellModal
				visible={cellModalVisible}
				onCancel={() => setCellModalVisible(false)}
				onSuccess={handleCellModalSuccess}
				userId={id}
				currentCellId={memberData?.cell_id?.id}
				userGender={memberData?.gender}
			/>

			{/* Record View Modals */}
			<ViewClassificationChangeRecordsModal
				visible={classificationRecordsVisible}
				onCancel={() =>
					setClassificationRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewStatusChangeRecordsModal
				visible={statusRecordsVisible}
				onCancel={() => setStatusRecordsVisible(false)}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewBaptismalStatusChangeRecordsModal
				visible={baptismalStatusRecordsVisible}
				onCancel={() =>
					setBaptismalStatusRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewCellChangeRecordsModal
				visible={cellRecordsVisible}
				onCancel={() => setCellRecordsVisible(false)}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewAffiliatedDepartmentChangeRecordsModal
				visible={departmentRecordsVisible}
				onCancel={() =>
					setDepartmentRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewDisciplinaryRecordsModal
				visible={disciplinaryRecordsVisible}
				onCancel={() =>
					setDisciplinaryRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<EditFamilyRelationshipModal
				visible={familyRelationshipModalVisible}
				onCancel={() =>
					setFamilyRelationshipModalVisible(false)
				}
				onSuccess={handleFamilyRelationshipModalSuccess}
				userId={id}
				userName={memberData?.name}
			/>
			<EditServingDepartmentModal
				visible={servingDepartmentModalVisible}
				onCancel={() =>
					setServingDepartmentModalVisible(false)
				}
				onSuccess={handleServingDepartmentModalSuccess}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewServingDepartmentChangeRecordsModal
				visible={servingDepartmentRecordsVisible}
				onCancel={() =>
					setServingDepartmentRecordsVisible(
						false
					)
				}
				userId={id}
				userName={memberData?.name}
			/>

			{/* Search Member Modal */}
			<SearchMemberModal
				visible={searchMemberModalVisible}
				onCancel={() =>
					setSearchMemberModalVisible(false)
				}
				onSelect={handleMemberSelect}
				excludeUserId={id}
			/>
		</>
	);
};

export default EditMemberData;
